/**
 * Главный роутер (data-router — createBrowserRouter).
 *
 * Используется data-router API (createBrowserRouter + RouterProvider в App.tsx),
 * а не <BrowserRouter>/<Routes>. Причина: хук useBlocker (защита от ухода с
 * несохранёнными правками в useUnsavedChanges) работает ТОЛЬКО внутри data-router
 * context. На обычном BrowserRouter useBlocker бросает
 * «useBlocker must be used within a data router» → белый экран на /files/transform.
 *
 * Защищённые роуты — pathless layout-route с element={<AuthGuard><Shell/></AuthGuard>};
 * Shell рендерит <Outlet/> для вложенных страниц.
 */
import { Navigate, createBrowserRouter } from 'react-router-dom';

import { AuthGuard } from './components/auth/AuthGuard';
import { Shell } from './components/layout/Shell';
import { PlaceholderPage } from './components/common/PlaceholderPage';
import { LoginPage } from './pages/LoginPage';
import { SettingsSystemPage } from './pages/SettingsSystemPage';
import { SettingsProfilePage } from './pages/SettingsProfilePage';
import { SettingsProjectsPage } from './pages/SettingsProjectsPage';
import { FilesUploadPage } from './pages/FilesUploadPage';
import { FilesTransformPage } from './pages/FilesTransformPage';
import { FilesResultsPage } from './pages/FilesResultsPage';
import { FilesTransformResultsPage } from './pages/FilesTransformResultsPage';
import { ProcessGenerationPage } from './pages/ProcessGenerationPage';
import { ProcessSearchPage } from './pages/ProcessSearchPage';
import { ProcessMatchingPage } from './pages/ProcessMatchingPage';
import { ProcessMediaPage } from "./pages/ProcessMediaPage";
import { LogsPage } from './pages/LogsPage';

export const router = createBrowserRouter([
  // Public
  { path: '/login', element: <LoginPage /> },

  // Protected (через Shell + AuthGuard). Pathless layout-route.
  {
    element: <AuthGuard><Shell /></AuthGuard>,
    children: [
      { path: '/', element: <Navigate to="/files/upload" replace /> },

      // Files (Phase 2)
      { path: '/files/upload', element: <FilesUploadPage /> },
      { path: '/files/transform', element: <FilesTransformPage /> },
      { path: '/files/results', element: <FilesResultsPage /> },
      { path: '/files/transform-results', element: <FilesTransformResultsPage /> },

      // Process
      { path: '/process/generation', element: <ProcessGenerationPage /> },
      { path: '/process/matching', element: <ProcessMatchingPage /> },
      { path: '/process/search', element: <ProcessSearchPage /> },
      { path: '/process/media', element: <ProcessMediaPage /> },
      { path: '/process/translations', element: <PlaceholderPage title="Translations" phase="Phase 4" /> },
      { path: '/process/complex', element: <PlaceholderPage title="Complex" phase="Phase 4+" /> },

      // Logs (P1.6 — Audit Log + Run History)
      { path: '/logs', element: <LogsPage /> },

      // Autoposting — отложено
      { path: '/autoposting/social', element: <PlaceholderPage title="Autoposting · Social" phase="Phase 6" /> },
      { path: '/autoposting/articles', element: <PlaceholderPage title="Autoposting · Articles" phase="Phase 6" /> },

      // Settings
      { path: '/settings/profile', element: <SettingsProfilePage /> },
      { path: '/settings/projects', element: <SettingsProjectsPage /> },
      { path: '/settings/billing', element: <PlaceholderPage title="Settings · Billing" phase="Phase 5" /> },
      { path: '/settings/system', element: <SettingsSystemPage /> },
      { path: '/settings/tips', element: <PlaceholderPage title="Settings · Tips" phase="Phase 4" /> },
    ],
  },

  // Fallback
  { path: '*', element: <Navigate to="/" replace /> },
]);